import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom'

import { DashboardTemplate } from '@cockpitai/components/Templates/DashboardTemplate'
import { isGroupVisionEnabled } from '@cockpitai/constants/featureFlags'
import { useLevelNavigation } from '@cockpitai/store/navigation/useLevelNavigation'
import { useSideBarStore } from '@cockpitai/store/navigation/useSideBarStore'
import { useSession } from '@cockpitai/store/session/useSession'
import { AuthTemplate } from '../components/Templates/AuthTemplate'

function StateInitializer({ children }: { children: React.ReactNode }) {
  const { isHydrated: isSessionHydrated } = useSession()
  const { isHydrated: isNavigationHydrated } = useLevelNavigation()
  const { isHydrated: isSidebarHydrated } = useSideBarStore()

  if (!isSessionHydrated || !isNavigationHydrated || !isSidebarHydrated) {
    return Array.from({ length: 2 }).map((_, index) => (
      <div key={index} className="h-80 w-full rounded-lg bg-gray-800"></div>
    ))
  }

  return <>{children}</>
}

function PrivateRoutes() {
  const { user, auth } = useSession()

  return user && auth?.token ? (
    <DashboardTemplate>
      <Outlet />
    </DashboardTemplate>
  ) : (
    <Navigate to="/" replace />
  )
}

function AuthRoutes() {
  const { user, auth } = useSession()

  return user && auth?.token ? (
    <Navigate to="/dashboard" replace />
  ) : (
    <AuthTemplate>
      <Outlet />
    </AuthTemplate>
  )
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <AuthRoutes />,
    children: [
      {
        path: '',
        lazy: async () => {
          const component = await import('../pages/Login')

          return { Component: component.default }
        },
      },
      {
        path: '/recuperar-senha',
        lazy: async () => {
          const component = await import('../pages/RecoveryPassword')

          return {
            Component: component.default,
          }
        },
      },
      {
        path: '/autenticacao',
        lazy: async () => {
          const component = await import('../pages/AuthToken')

          return {
            Component: component.default,
          }
        },
      },
    ],
  },
  {
    path: '/dashboard',
    element: <PrivateRoutes />,
    children: [
      {
        path: '',
        lazy: async () => {
          const component = await import('../pages/dashboard/Home')

          return { Component: component.default }
        },
      },
      {
        path: 'chat',

        lazy: async () => {
          const component = await import('../pages/dashboard/Chat')

          return { Component: component.default }
        },
      },
      {
        path: 'chat/:id',

        lazy: async () => {
          const component = await import('../pages/dashboard/Chat/History')

          return { Component: component.default }
        },
      },
      {
        path: 'big-questions',
        lazy: async () => {
          const component = await import('../pages/dashboard/BigQuestions')

          return { Component: component.default }
        },
      },
      {
        path: 'big-views',
        lazy: async () => {
          const component = await import('../pages/dashboard/BigViews')

          return { Component: component.default }
        },
      },
      {
        path: 'big-numbers',
        lazy: async () => {
          const component = await import('../pages/dashboard/BigNumbers')

          return { Component: component.default }
        },
      },
      {
        path: 'conversions',
        lazy: async () => {
          const component = await import('../pages/dashboard/Conversion')

          return { Component: component.default }
        },
      },
      {
        path: 'market-share',
        lazy: async () => {
          const component = await import('../pages/dashboard/Market')

          return { Component: component.default }
        },
      },
      {
        path: 'my-panels',

        lazy: async () => {
          const component = await import('../pages/dashboard/MyPanels')

          return { Component: component.default }
        },
      },
      {
        path: 'hour-by-hour',
        lazy: async () => {
          const component = await import('../pages/dashboard/HourByHour')

          return { Component: component.default }
        },
      },
      {
        path: 'fidelity',
        lazy: async () => {
          const component = await import('../pages/dashboard/Fidelity')

          return { Component: component.default }
        },
      },
      {
        path: 'proposals',
        lazy: async () => {
          const component = await import('../pages/dashboard/Proposals')

          return { Component: component.default }
        },
      },
      ...(isGroupVisionEnabled
        ? [
            {
              path: 'grupos/:groupCode',
              lazy: async () => {
                const component = await import('../pages/dashboard/GroupDashboard')

                return { Component: component.default }
              },
            },
          ]
        : [
            {
              path: 'grupos/:groupCode',
              element: <Navigate to="/dashboard" replace />,
            },
          ]),
    ],
  },
])

export function Routes() {
  return (
    <StateInitializer>
      <RouterProvider router={router} />
    </StateInitializer>
  )
}
